import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'
import { useTranslation } from 'react-i18next'
import useUserContext from 'shared/users/contexts/useUserContext'
import useTabs from 'shared/tabs/useTabs'
import { parseApiError } from 'shared/api/apiError'
import notifyServerError from 'shared/api/notifyServerError'
import { Tab, TRANSIT_EDITOR_TABS } from 'features/declaration/form/components/editorTabs'
import useDeclarationEditor, { FormActionResult } from './useDeclarationEditor'
import useCancellationReasonModal from '../../transit-operation/components/useCancellationReasonModal'
import useGrossMassCheckConfirmation from './useGrossMassValidationConfirmation'
import useRequestReleaseModal from '../../transit-operation/components/useRequestReleaseModal'
import useReleaseNonArrivedMovementModal from '../../transit-operation/components/useReleaseNonArrivedMovementModal'
import useCustomsResponseModal from '../../transit-operation/components/useCustomsResponseModal'
import useCustomsContactModal from '../../transit-operation/components/useCustomsContactModal'
import { TransitEditorAction } from '../../models/types'
import { getErrorMessage } from '../../../../helpers/errors.helper'
import { isApiError } from '../../../../errors/ApiError'
import { ErrorResponse, parseErrorResponse } from '../../../../shared/api/parseAxiosTransitErrorResponse'
import { useNctsErrors } from '../contexts/NctsErrorContext'
import ROUTES from '../../../../config/routes'
import { requiresSignedSubmission } from '../../../../config'
import useDeclarationApiErrorNotifications from './useDeclarationApiErrorNotifications'
import useDuplicateTransitOperationModal from '../../transit-operation/components/useDuplicateTransitOperationModal'
import { useDeclarationEditorContext } from '../contexts/DeclarationEditorContext'
import { useConfirm } from '../../../../shared/components/confirm/ConfirmProvider'
import useCreateTransitFromEnsModal from '../../transit-operation/components/useCreateTransitFromEnsModal'
import useEnsAssessmentWarning from './useEnsAssessmentWarning'
import useRestrictionWarning from './useRestrictionWarning'

// Audit routing keeps the declaration in Tardek: nothing reached customs yet.
const REVIEW_STATUSES = ['IN_REVIEW', 'CHANGE_REVIEW']

const isHeldForReview = (status?: string | null) => !!status && REVIEW_STATUSES.includes(status)

function useDeclarationEditorActions({ isEns } : { isEns: boolean }) {
  const { declarationData, editorData } = useDeclarationEditorContext()

  const { t } = useTranslation()
  const navigate = useNavigate()

  const { confirm } = useConfirm()
  const transitOperationId = declarationData?.transitOperation?.id ?? null

  const grossMassCheckModalOps = useGrossMassCheckConfirmation()
  const requestReleaseModalOps = useRequestReleaseModal(transitOperationId)
  const nonArrivedMovementReleaseModalOps = useReleaseNonArrivedMovementModal(transitOperationId)
  const customsResponseModalOps = useCustomsResponseModal(transitOperationId)
  const customsContactModalOps = useCustomsContactModal(transitOperationId)
  const duplicationModal = useDuplicateTransitOperationModal()
  const createTransitFromEnsModal = useCreateTransitFromEnsModal()
  const cancellationReasonModalOps = useCancellationReasonModal()
  const { confirmEnsPresentationNotification } = useEnsAssessmentWarning(transitOperationId, editorData?.country ?? null)
  const { confirmRestrictionWarning } = useRestrictionWarning()

  const editor = useDeclarationEditor({
    isEns,
    checkGrossMass: grossMassCheckModalOps.checkGrossMass,
    requestCancellationReason: cancellationReasonModalOps.requestCancellationReason,
    confirmEnsPresentationNotification,
    confirmRestrictionWarning,
  })

  const { isAdmin } = useUserContext()

  const { activeTab, goToTab } = useTabs<Tab>({
    tabs: TRANSIT_EDITOR_TABS,
    defaultTab: 'general',
    isReady: !editor.isLoading,
    isEnabled: (tab: Tab) => {
      if (tab === 'house-consignments') {
        return !!declarationData?.consignment?.id
      }
      if (tab === 'directo') {
        return isAdmin
      }
      return true
    },
    canLeave: async (current: Tab, next: Tab): Promise<boolean> => {
      if (next !== 'directo' && editor.directoTabRef.current?.isDirty()) {
        return await confirm({
          message: 'Directo form has unsaved changes. Leave without saving?',
        })
      }

      if (['general', 'transit-parties'].includes(current) && next !== 'general' && next !== 'transit-parties') {
        const { formState: { isDirty } } = editor.form
        if (isDirty) {
          const saveResult = await editor.validateAndSaveTransitOperationDraft()
          if (!saveResult.success) {
            const apiError = saveResult.errorReason === 'apiError' ? parseApiError(saveResult.error) : null
            if (apiError?.status !== 503 && apiError?.reason === 'serverError' && apiError.traceId) {
              notifyServerError(apiError.traceId)
            } else {
              toast.error(t('transitOperation.draft.saveError'))
            }
            return false
          }

          toast.success(t('transitOperation.draft.saveSuccess'))
        }
      }

      return true
    },
  })

  const { formState: { isDirty } } = editor.form

  const { handleTransitApiError } = useDeclarationApiErrorNotifications({ goToTab })
  const { setNctsErrors } = useNctsErrors()

  const showErrorNotificationForFormActionResult = async (result: FormActionResult) => {
    if (!result.success) {
      switch (result.errorReason) {
        case 'draftSaveFailed':
          toast.error(t('transitOperation.draft.saveError'))
          break
        case 'nctsValidationError':
          toast.error('NCTS validation errors')
          await goToTab('summary')
          break
        case 'formErrors':
          toast.error('Form contains errors')
          if (result.nctsErrors?.length) {
            await goToTab('summary')
          }
          break
        case 'apiError': {
          const apiError = parseApiError(result.error)
          if (apiError.status !== 503 && apiError.reason === 'serverError' && apiError.traceId) {
            notifyServerError(apiError.traceId)
            break
          }
          await handleTransitApiError(result.error, result.errorReason)
          break
        }
        case 'userCancel':
        case 'invalidState':
          break
        case 'downloadError': {
          const downloadErrorResponse = isApiError(result.error)
            ? parseErrorResponse(result.error.details as ErrorResponse, result.error.status)
            : undefined

          if (downloadErrorResponse?.isNctsError) {
            await goToTab('summary')
            setNctsErrors(downloadErrorResponse.nctsErrors.map((nctsApiError) => ({
              message: nctsApiError.description,
              path: nctsApiError.field,
            })))
          }

          toast.error(getErrorMessage(result.error) ?? 'Download failed')
          break
        }
        default:
          // eslint-disable-next-line no-console
          console.error('Unhandled error occurred', result)
          break
      }
    }
  }

  const showSuccessNotificationForEditorAction = (action: TransitEditorAction, result: FormActionResult) => {
    switch (action) {
      case 'saveDraft':
        toast.success(t('transitOperation.draft.saveSuccess'))
        break
      case 'validate':
        toast.success('NCTS rules validation was successful')
        break
      case 'submit':
      case 'amend':
      case 'cancel':
        if (isHeldForReview(result.transit?.transitOperation?.status)) {
          toast.warn(action === 'cancel'
            ? t('transitOperation.cancel.requestSentToReview')
            : t('transitOperation.submission.sentToReview'))
        } else if (action === 'cancel') {
          toast.success(t('transitOperation.cancel.requestSentToCustoms'))
        } else {
          toast.success(requiresSignedSubmission(editorData?.country)
            ? t('transitOperation.submission.awaitingSigning')
            : t('transitOperation.submission.sentToCustoms'))
        }
        break
      case 'generateXml':
      case 'taxesPdf':
      case 'taxesXlsx':
        toast.success('Download was successful')
        break
      default:
        break
    }
  }

  const isUnsavedChanges = () => isDirty || !!editor.directoTabRef?.current?.isDirty()

  const runEditorAction = async (action: TransitEditorAction) => {
    switch (action) {
      case 'duplicate':
        if (!transitOperationId) {
          throw new Error('Missing transit operation ID')
        }

        if (isUnsavedChanges()) {
          const ok = await confirm({
            message: 'Form has unsaved changes. Do you wish to continue anyway?',
          })
          if (!ok) {
            return true
          }
        }

        duplicationModal.open(transitOperationId)
        break
      case 'createNew':
        if (isUnsavedChanges()) {
          const ok = await confirm({
            title: t('declaration.p5.createNew'),
            message: t('declaration.p5.createNewWarning'),
          })
          if (!ok) {
            return true
          }
        }

        navigate(isEns ? ROUTES.newEns : ROUTES.newTransitP5)
        break
      case 'createTransitFromEns':
        if (!transitOperationId) {
          throw new Error('Missing transit operation ID')
        }

        if (isUnsavedChanges()) {
          const ok = await confirm({
            message: 'Form has unsaved changes. Do you wish to continue anyway?',
          })
          if (!ok) {
            return true
          }
        }

        createTransitFromEnsModal.open(transitOperationId)
        break
      case 'requestRelease':
        requestReleaseModalOps.open()
        break
      case 'nonArrivedMovementRelease':
        nonArrivedMovementReleaseModalOps.open()
        break
      case 'invalidationResponse':
        customsResponseModalOps.open()
        break
      case 'contact':
        customsContactModalOps.open()
        break
      default:
        return false
    }

    return true
  }

  const invokeEditorAction = async (action: TransitEditorAction): Promise<boolean> => {
    const actionCompleted = await runEditorAction(action)
    if (actionCompleted) return false

    const result = await editor.invokeFormAction(action)

    const createdTransitOperationId = transitOperationId ? undefined : result.transit?.transitOperation?.id
    if (createdTransitOperationId) {
      const redirectPath = isEns ? ROUTES.ens : ROUTES.transitP5
      navigate(`${redirectPath}/${createdTransitOperationId}`)
    }

    if (!result.success) {
      await showErrorNotificationForFormActionResult(result)
      return false
    }

    if (createdTransitOperationId) {
      toast.success(isEns ? t('declaration.ens.created') : t('declaration.transit.created'))
      return true
    }

    showSuccessNotificationForEditorAction(action, result)

    return true
  }

  return {
    form: editor.form,
    declaration: editor.transit,
    activeTab,
    goToTab,
    directoTabRef: editor.directoTabRef,
    interactionsLocked: editor.interactionsLocked,
    country: editorData?.country ?? null,

    invokeEditorAction,

    cancellationReasonModalOps,
    grossMassCheckModalOps,
    requestReleaseModalOps,
    nonArrivedMovementReleaseModalOps,
    customsResponseModalOps,
    customsContactModalOps,
    duplicationModal,
    createTransitFromEnsModal,
  }
}

export default useDeclarationEditorActions
